import { create } from 'zustand';
import {
  EscalaFuente,
  FuenteFamilia,
  TemaUI,
  aplicarUIPrefs,
  parseEscala,
  parseFuenteFamilia,
  parseTema,
} from '../utils/uiPrefs';

/**
 * Sprint 40 — Store de preferencias UI (escala fuente + tema).
 * Sprint 48 — Agrega selección de familia de fuente.
 *
 * Persistido en `ConfiguracionSistema` (key-value), mismo patrón que
 * tours/SMTP/backups. Single-tenant: la pref es del negocio, no del
 * usuario (un monitor, una dueña + meseros sobre el mismo equipo).
 */
interface UIPrefsState {
  escala: EscalaFuente;
  tema: TemaUI;
  fuente: FuenteFamilia;
  cargada: boolean;
  /**
   * Sprint 56.3 — Motivo del último guardado fallido, o null.
   *
   * Antes el `await config.actualizar(...)` se ignoraba: si el backend
   * rechazaba la clave, la UI aplicaba el cambio en pantalla y el usuario sólo
   * se enteraba al reiniciar y ver que no se había guardado nada.
   */
  errorGuardado: string | null;
  cargar: () => Promise<void>;
  setEscala: (e: EscalaFuente) => Promise<void>;
  setTema: (t: TemaUI) => Promise<void>;
  setFuente: (f: FuenteFamilia) => Promise<void>;
}

const KEY_ESCALA = 'ui_escala_fuente';
const KEY_TEMA = 'ui_tema';
const KEY_FUENTE = 'ui_fuente_familia';

export const useUIPrefsStore = create<UIPrefsState>((set, get) => ({
  escala: 'normal',
  tema: 'claro',
  fuente: 'inter',
  cargada: false,
  errorGuardado: null,
  cargar: async () => {
    const [resE, resT, resF] = await Promise.all([
      window.api.config.obtener(KEY_ESCALA),
      window.api.config.obtener(KEY_TEMA),
      window.api.config.obtener(KEY_FUENTE),
    ]);
    const escala = parseEscala(resE.ok ? resE.data : null);
    const tema = parseTema(resT.ok ? resT.data : null);
    const fuente = parseFuenteFamilia(resF.ok ? resF.data : null);
    aplicarUIPrefs(escala, tema, fuente);
    set({ escala, tema, fuente, cargada: true });
  },
  setEscala: async (e) => {
    const { tema, fuente } = get();
    aplicarUIPrefs(e, tema, fuente);
    set({ escala: e });
    set({ errorGuardado: await persistir(KEY_ESCALA, e) });
  },
  setTema: async (t) => {
    const { escala, fuente } = get();
    aplicarUIPrefs(escala, t, fuente);
    set({ tema: t });
    set({ errorGuardado: await persistir(KEY_TEMA, t) });
  },
  setFuente: async (f) => {
    const { escala, tema } = get();
    aplicarUIPrefs(escala, tema, f);
    set({ fuente: f });
    set({ errorGuardado: await persistir(KEY_FUENTE, f) });
  },
}));

/**
 * Guarda una preferencia y devuelve el motivo del fallo (null si todo bien).
 * El cambio ya se aplicó en pantalla: aquí sólo importa si sobrevivirá al
 * próximo arranque.
 */
async function persistir(clave: string, valor: string): Promise<string | null> {
  try {
    const res = await window.api.config.actualizar(clave, valor);
    if (res.ok) return null;
    return res.error ?? 'No se pudo guardar la preferencia.';
  } catch {
    return 'No se pudo guardar la preferencia.';
  }
}
